import { css, type SerializedStyles } from '@emotion/react';

import {
  SETTINGS_BODY_TEXT_STYLE,
  SETTINGS_CAPTION_STYLE,
  SETTINGS_PANEL_HEADER_STYLE,
  SETTINGS_PANEL_STYLE,
  SETTINGS_SPACING,
  SETTINGS_TOGGLE_KNOB_OFF_STYLE,
  SETTINGS_TOGGLE_KNOB_STYLE,
  SETTINGS_TOGGLE_TRACK_OFF_STYLE,
  SETTINGS_TOGGLE_TRACK_STYLE,
} from '@/Client/Styles';
import type { StyleOverride } from '@kaiizen/kai-shiki/react';
import { DEFAULT_ORG_SETTING_CHIP_STYLES, OrgSettingChipStyles } from './orgSettingChipStyles';
import { DEFAULT_ORG_SETTING_DIALOG_STYLES, OrgSettingDialogStyles } from './orgSettingDialogStyles';

export type AccessToggleState = 'on' | 'off';

export type SuperAdminSupplierAccessManagerStyles = {
  root: SerializedStyles;
  status: SerializedStyles;
  supplierPanel: SerializedStyles;
  supplierHeader: SerializedStyles;
  supplierMeta: SerializedStyles;
  controls: SerializedStyles;
  supplierBody: SerializedStyles;
  orgSettingsHeading: SerializedStyles;
  orgChips: SerializedStyles;
  addOrgRow: SerializedStyles;
  // CommonToggle is Emotion-free: it applies these as inline style objects, so they stay plain objects, not css() results.
  toggleTrackByState: Record<AccessToggleState, StyleOverride>;
  toggleKnobByState: Record<AccessToggleState, StyleOverride>;
  orgChip: OrgSettingChipStyles;
  orgDialog: OrgSettingDialogStyles;
};

const ROW = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const DEFAULT_SUPER_ADMIN_SUPPLIER_ACCESS_MANAGER_STYLES: SuperAdminSupplierAccessManagerStyles = {
  root: css`
    label: SUPPLIER_ACCESS;
    width: 100%;
    padding: ${SETTINGS_SPACING.sectionInsetTop}px ${SETTINGS_SPACING.gutterX}px 0;
    display: flex;
    flex-direction: column;
    gap: 18px;
  `,
  status: css`
    label: SUPPLIER_ACCESS_STATUS;
    ${SETTINGS_BODY_TEXT_STYLE};
  `,
  supplierPanel: css`
    label: SUPPLIER_ACCESS_PANEL;
    ${SETTINGS_PANEL_STYLE};
  `,
  supplierHeader: css`
    label: SUPPLIER_ACCESS_HEADER;
    ${SETTINGS_PANEL_HEADER_STYLE};
    ${ROW};
  `,
  supplierMeta: css`
    label: SUPPLIER_ACCESS_META;
    ${SETTINGS_CAPTION_STYLE};
  `,
  controls: css`
    label: SUPPLIER_ACCESS_CONTROLS;
    ${ROW};
  `,
  supplierBody: css`
    label: SUPPLIER_ACCESS_BODY;
    padding: 12px ${SETTINGS_SPACING.gutterX}px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  `,
  orgSettingsHeading: css`
    label: SUPPLIER_ACCESS_ORG_SETTINGS_HEADING;
    ${SETTINGS_CAPTION_STYLE};
  `,
  orgChips: css`
    label: SUPPLIER_ACCESS_ORG_CHIPS;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  `,
  addOrgRow: css`
    label: SUPPLIER_ACCESS_ADD_ORG;
    ${ROW};
  `,
  toggleTrackByState: {
    on: [SETTINGS_TOGGLE_TRACK_STYLE],
    off: [SETTINGS_TOGGLE_TRACK_STYLE, SETTINGS_TOGGLE_TRACK_OFF_STYLE],
  },
  toggleKnobByState: {
    on: [SETTINGS_TOGGLE_KNOB_STYLE],
    off: [SETTINGS_TOGGLE_KNOB_STYLE, SETTINGS_TOGGLE_KNOB_OFF_STYLE],
  },
  orgChip: DEFAULT_ORG_SETTING_CHIP_STYLES,
  orgDialog: DEFAULT_ORG_SETTING_DIALOG_STYLES,
};
